import { notification } from 'antd';
import { all, call, put, take, select, fork } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import update from 'immutability-helper';
import * as moment from 'moment';
import { isObjectEmpty } from 'utils';
import {
  LOAD_TIME_SLOT_LIST,
  HOLD_BOOKING_SLOT,
  RELEASE_BOOKING_SLOT,
  BOOK_APPOINTMENT,
  SAVE_SELECTED_BOOKING_SLOT,
  CLEAR_SELECTED_BOOKING_SLOT,
} from '../type';
import {
  loadTimeSlotListSuccessAction,
  loadTimeSlotListErrorAction,
  holdBookingSlotSuccessAction,
  holdBookingSlotErrorAction,
  releaseBookingSlotSuccessAction,
  releaseBookingSlotErrorAction,
  bookAppointmentSuccessAction,
  bookAppointmentErrorAction,
  saveSelectedBookingSlotSuccessAction,
  saveSelectedBookingSlotErrorAction,
  clearSelectedBookingSlotAction,
  clearSelectedBookingSlotSuccessAction,
  clearSelectedBookingSlotErrorAction,
} from '../action';

import { getTimeSlotList } from 'store/fittest_booking/selector';

import { history } from 'store';

import {
  bookingService,
  fittestBookingService,
  patientService,
} from 'services';

let fittestBookingTimeSlot;

function subscribe(socket) {
  return new eventChannel(emit => {
    const socketHandler = (topic, message) => {
      if (!fittestBookingTimeSlot) {
        return;
      }
      let data = JSON.parse(message.toString());
      if (data.holdCalendarIDs && Array.isArray(data.holdCalendarIDs)) {
        data.holdCalendarIDs.forEach(holdId => {
          for (let date in fittestBookingTimeSlot) {
            fittestBookingTimeSlot[date].forEach(timeSlot => {
              if (timeSlot.calendarIdList.indexOf(holdId) >= 0) {
                if (!timeSlot.lockID) {
                  timeSlot.lockID = {};
                }
                if (!timeSlot.lockID[holdId]) {
                  timeSlot.lockID[holdId] = 1;
                } else {
                  timeSlot.lockID[holdId] += 1;
                }
                if (timeSlot.lockID[holdId] >= timeSlot.availableBooking) {
                  timeSlot.lockSlot = true;
                }
              }
            });
          }
        });
      }
      if (data.releaseCalendars) {
        for (let uuid in data.releaseCalendars) {
          for (let date in fittestBookingTimeSlot) {
            for (let i = 0; i < fittestBookingTimeSlot[date].length; i++) {
              let timeSlot = fittestBookingTimeSlot[date][i];
              if (!timeSlot.lockID) {
                continue;
              }
              let max = Number.MIN_SAFE_INTEGER;
              let hasReleased = false;
              for (let i = 0; i < timeSlot.calendarIdList.length; i++) {
                let calendarId = timeSlot.calendarIdList[i];
                if (!timeSlot.lockID[calendarId]) {
                  timeSlot.lockID[calendarId] = 0;
                  max = Math.max(max, 0);
                  continue;
                }
                if (data.releaseCalendars[uuid].indexOf(calendarId) >= 0) {
                  timeSlot.lockID[calendarId] -= 1;
                  hasReleased = true;
                }
                max = Math.max(max, 0);
              }
              if (hasReleased && max < timeSlot.availableBooking) {
                timeSlot.lockSlot = false;
              }
            }
          }
        }
      }
      emit({
        bookingData: fittestBookingTimeSlot,
        realeaseUUID:
          (data.releaseCalendars && Object.keys(data.releaseCalendars)) || [],
      });
    };
    socket.on('message', socketHandler);
    return () => {};
  });
}

function* read(socket) {
  const channel = yield call(subscribe, socket);
  while (true) {
    let responseChannel = yield take(channel);
    if (responseChannel && !isObjectEmpty(responseChannel.bookingData)) {
      yield put(
        loadTimeSlotListSuccessAction({
          bookingData: { ...responseChannel.bookingData },
          releaseUUID: responseChannel.realeaseUUID,
        }),
      );
    }
  }
}

function* loadTimeSlotList() {
  while (1) {
    try {
      const action = yield take(LOAD_TIME_SLOT_LIST);
      const socket = action.payload.socketClient;
      const requestSocket = action.payload.requestSocket;
      const bookingSlotData = yield call(
        fittestBookingService.loadBookingSlotList,
        action.payload,
      );
      yield put(
        loadTimeSlotListSuccessAction({
          bookingData: bookingSlotData,
          releaseUUID: [],
        }),
      );
      const bookingTimeSlot = yield select(getTimeSlotList);
      fittestBookingTimeSlot = bookingTimeSlot.bookingData;
      if (socket && requestSocket) {
        yield fork(read, socket);
      }
    } catch (err) {
      notification.error({
        message: err.message,
      });
      yield put(loadTimeSlotListErrorAction(err));
    }
  }
}

function* holdBookingSlot() {
  while (1) {
    const action = yield take(HOLD_BOOKING_SLOT);
    try {
      const holdBookingSlotResponse = yield call(
        bookingService.holdBookingSlot,
        action.payload,
      );
      if (holdBookingSlotResponse && holdBookingSlotResponse.success) {
        yield put(holdBookingSlotSuccessAction(holdBookingSlotResponse));
      } else {
        if (action.payload.callback) {
          action.payload.callback();
        }
        throw new Error(holdBookingSlotResponse.message);
      }
    } catch (err) {
      notification.error({
        message: err.message,
      });
      yield put(holdBookingSlotErrorAction(err));
    }
  }
}

function* releaseBookingSlot() {
  while (1) {
    const action = yield take(RELEASE_BOOKING_SLOT);
    try {
      const holdBookingSlotResponse = yield call(
        bookingService.releaseBookingSlot,
        action.payload,
      );
      yield put(releaseBookingSlotSuccessAction(holdBookingSlotResponse));
    } catch (err) {
      notification.error({
        message: err.message,
      });
      yield put(releaseBookingSlotErrorAction(err));
    }
  }
}

function* saveSelectedBookingSlot() {
  while (1) {
    const action = yield take(SAVE_SELECTED_BOOKING_SLOT);
    try {
      yield put(saveSelectedBookingSlotSuccessAction(action.payload));
    } catch (err) {
      yield put(saveSelectedBookingSlotErrorAction(err));
    }
  }
}

function* bookAppointment() {
  while (1) {
    const action = yield take(BOOK_APPOINTMENT);
    const values = action.payload;

    let organisationId,
      patientId = null;

    patientId = values.generalCandidateId;

    organisationId = values.organisationId;
    const callBack = action.payload.callback;
    try {
      const sampleCaseData = {
        patientId,
        organisationId,
        caseProblemName: 'PEM - Fit Test',
        dateInjury: moment(new Date()).toISOString(),
      };
      const caseInfo = yield call(
        patientService.createPatientCase,
        sampleCaseData,
      );

      const caseId = caseInfo.id;

      const updatedValues = update(values, {
        caseId: { $set: caseId },
      });

      const bookedAppointment = yield call(
        bookingService.bookAppointment,
        updatedValues,
      );

      const appointmentId = bookedAppointment.appointmentId;
      organisationId = bookedAppointment.organisationId;

      if (callBack) {
        callBack();
      }

      yield put(bookAppointmentSuccessAction(bookedAppointment));
      yield put(clearSelectedBookingSlotAction());
      notification.success({
        message: 'New appointment has been successfully booked',
      });

      history.push(
        `/manage-booking/${appointmentId}/organisation/${organisationId}`,
      );
    } catch (err) {
      notification.error({
        message: err.message,
      });
      yield put(bookAppointmentErrorAction(err));
    }
  }
}

function* clearSelectedBookingSlot() {
  while (1) {
    const action = yield take(CLEAR_SELECTED_BOOKING_SLOT);
    try {
      yield put(clearSelectedBookingSlotSuccessAction(action.payload));
    } catch (err) {
      yield put(clearSelectedBookingSlotErrorAction(err));
    }
  }
}

export default function* rootSaga() {
  yield all([
    bookAppointment(),
    releaseBookingSlot(),
    holdBookingSlot(),
    loadTimeSlotList(),
    saveSelectedBookingSlot(),
    clearSelectedBookingSlot(),
  ]);
}
